/**
 * RealTimeLipSyncTap — UPGRADED v2.1
 *
 * v2.1 changes vs your v2.0:
 *  1) Formant stability: tiny spectrum smoothing + gating (only when voiced & strong enough)
 *  2) HNR performance: compute every N frames + smooth + optional shorter buffer
 *  3) Plosive burst: adaptive low-band burst threshold using rolling baseline
 *  4) WWW kept: allow voiced WWW, but make it brief-limited + cooldown to prevent camping
 */

export class RealTimeLipSyncTap {
  constructor(audioCtx, analyser, lips, opts) {
    opts = opts || {};
    this.audioCtx = audioCtx;
    this.analyser = analyser;
    this.lips = lips;

    this.debug = opts.debug === undefined ? true : !!opts.debug;

    // Analysis cadence / FFT
    this.fftSize = opts.fftSize === undefined ? 2048 : opts.fftSize;
    this.smoothing = opts.smoothing === undefined ? 0.55 : opts.smoothing;
    this.frameMs = opts.frameMs === undefined ? 33 : opts.frameMs;

    // Loudness & gating
    this.minRms = opts.minRms === undefined ? 0.015 : opts.minRms;
    this.hissRatio = opts.hissRatio === undefined ? 1.35 : opts.hissRatio;
    this.intensityGain =
      opts.intensityGain === undefined ? 1.2 : opts.intensityGain;
    this.debounceMs = opts.debounceMs === undefined ? 35 : opts.debounceMs;
    this.minChangeMs = opts.minChangeMs === undefined ? 25 : opts.minChangeMs;

    // Hysteresis
    this.gateOpenRms =
      opts.gateOpenRms === undefined ? this.minRms * 1.6 : opts.gateOpenRms;
    this.gateCloseRms =
      opts.gateCloseRms === undefined ? this.minRms * 1.1 : opts.gateCloseRms;
    this.silenceHoldMs =
      opts.silenceHoldMs === undefined ? 90 : opts.silenceHoldMs;

    // Idle auto-stop
    this.idleStopMs = opts.idleStopMs === undefined ? null : opts.idleStopMs;

    // Auto-calibration
    this.autoCalibrate =
      opts.autoCalibrate === undefined ? true : !!opts.autoCalibrate;
    this.calibrateMs = opts.calibrateMs === undefined ? 450 : opts.calibrateMs;
    this._calibrated = false;

    // ZCR / voicing
    this.useZcr = opts.useZcr === undefined ? true : !!opts.useZcr;
    this.zcrThreshold =
      opts.zcrThreshold === undefined ? 0.12 : opts.zcrThreshold;
    this.centroidShift =
      opts.centroidShift === undefined ? 0 : opts.centroidShift;

    // HNR voicing detection via autocorrelation
    this.useHnr = opts.useHnr === undefined ? true : !!opts.useHnr;
    this.hnrVoicedThreshold =
      opts.hnrVoicedThreshold === undefined ? 0.22 : opts.hnrVoicedThreshold;

    // NEW v2.1: HNR throttling + smoothing
    this.hnrEveryNFrames =
      opts.hnrEveryNFrames === undefined ? 3 : opts.hnrEveryNFrames; // compute every 3 frames
    this.hnrSmooth = opts.hnrSmooth === undefined ? 0.7 : opts.hnrSmooth; // 0..0.95
    this.hnrBufferSamples =
      opts.hnrBufferSamples === undefined ? 1024 : opts.hnrBufferSamples; // shorter autocorr buffer
    this._hnrFrame = 0;
    this._hnrSmoothed = 0.0;

    // Jaw / mouth-open driver (MOUTH overlay)
    this.enableMouthDrive =
      opts.enableMouthDrive === undefined ? false : !!opts.enableMouthDrive;
    this.mouthDriveGain =
      opts.mouthDriveGain === undefined ? 0.9 : opts.mouthDriveGain;
    this.mouthAttackMs =
      opts.mouthAttackMs === undefined ? 40 : opts.mouthAttackMs;
    this.mouthReleaseMs =
      opts.mouthReleaseMs === undefined ? 180 : opts.mouthReleaseMs;
    this._mouthEnv = 0;

    // MBP onset pop (plosives)
    this.enableOnsetMbp =
      opts.enableOnsetMbp === undefined ? true : !!opts.enableOnsetMbp;
    this.onsetRmsDelta =
      opts.onsetRmsDelta === undefined ? 0.012 : opts.onsetRmsDelta;
    this.onsetCooldownMs =
      opts.onsetCooldownMs === undefined ? 110 : opts.onsetCooldownMs;
    this._lastRms = 0;
    this._lastOnsetAt = 0;

    // v2.0: Plosive burst detection — (we will replace absolute threshold with adaptive)
    this.burstLoThreshold =
      opts.burstLoThreshold === undefined ? 0.45 : opts.burstLoThreshold;

    // NEW v2.1: Adaptive low-band burst baseline
    this._loBase = 0;
    this.loBaseSmooth =
      opts.loBaseSmooth === undefined ? 0.92 : opts.loBaseSmooth;
    this.burstLoFactor =
      opts.burstLoFactor === undefined ? 1.6 : opts.burstLoFactor;

    // Score smoothing
    this.smoothScores =
      opts.smoothScores === undefined ? true : !!opts.smoothScores;
    this.scoreDecay = opts.scoreDecay === undefined ? 0.78 : opts.scoreDecay;
    this.scoreBoost = opts.scoreBoost === undefined ? 1.0 : opts.scoreBoost;

    // Separate vowel and consonant score pools
    this._vowelScores = { AAA: 0, EH: 0, OHH: 0, UUU: 0, IEE: 0 };
    this._consScores = {
      RRR: 0,
      WWW: 0,
      SSS: 0,
      FFF: 0,
      TTH: 0,
      TLDN: 0,
      MBP: 0,
    };

    // Per-phoneme bias
    this.phonemeBias = opts.phonemeBias || {
      AAA: 1.0,
      EH: 1.0,
      OHH: 1.0,
      UUU: 1.0,
      IEE: 1.0,
      RRR: 1.0,
      WWW: 0.85,
      SSS: 1.0,
      FFF: 1.0,
      TTH: 1.0,
      TLDN: 1.0,
      MBP: 1.0,
    };

    // F1/F2 boundaries
    this.f1Low = opts.f1Low === undefined ? 400 : opts.f1Low;
    this.f1Mid = opts.f1Mid === undefined ? 750 : opts.f1Mid;
    this.f2Low = opts.f2Low === undefined ? 1100 : opts.f2Low;
    this.f2Mid = opts.f2Mid === undefined ? 1800 : opts.f2Mid;
    this.f2High = opts.f2High === undefined ? 2600 : opts.f2High;

    // Vowel centroid fallback boundaries
    this.vowelC1 = opts.vowelC1 === undefined ? 900 : opts.vowelC1;
    this.vowelC2 = opts.vowelC2 === undefined ? 1500 : opts.vowelC2;
    this.vowelC3 = opts.vowelC3 === undefined ? 2400 : opts.vowelC3;

    // WWW limiter
    this.wwwLoOverMid =
      opts.wwwLoOverMid === undefined ? 1.5 : opts.wwwLoOverMid;
    this.wwwCentroidMax =
      opts.wwwCentroidMax === undefined ? 1200 : opts.wwwCentroidMax;

    // NEW v2.1: WWW brief limit + cooldown (keep WWW but prevent camping)
    this.wwwMaxFrames = opts.wwwMaxFrames === undefined ? 3 : opts.wwwMaxFrames; // allow WWW for only a few frames
    this.wwwCooldownMs =
      opts.wwwCooldownMs === undefined ? 140 : opts.wwwCooldownMs;
    this._wwwFrames = 0;
    this._lastWwwAt = 0;

    // Rounded vowel detector
    this.roundLoOverMid =
      opts.roundLoOverMid === undefined ? 1.28 : opts.roundLoOverMid;
    this.roundCentroidPad =
      opts.roundCentroidPad === undefined ? 150 : opts.roundCentroidPad;

    // Variance-based noise gate
    this.useVarianceGate =
      opts.useVarianceGate === undefined ? true : !!opts.useVarianceGate;
    this.varianceWindowSize =
      opts.varianceWindowSize === undefined ? 8 : opts.varianceWindowSize;
    this.varianceMinThresh =
      opts.varianceMinThresh === undefined ? 3e-6 : opts.varianceMinThresh;
    this._rmsHistory = [];

    // Anti-camping
    this._consecutiveWins = {};
    this._maxConsecutive =
      opts.maxConsecutive === undefined ? 12 : opts.maxConsecutive;

    // NEW v2.1: Formant stability helpers
    this.formantMinIntensity =
      opts.formantMinIntensity === undefined ? 0.5 : opts.formantMinIntensity;
    this.formantSmoothBins =
      opts.formantSmoothBins === undefined ? 1 : opts.formantSmoothBins; // 1 = 3-bin average
    this._lastFormants = { f1: 0, f2: 0, f3: 0 };
    this.formantTemporalSmooth =
      opts.formantTemporalSmooth === undefined
        ? 0.65
        : opts.formantTemporalSmooth; // 0..0.95

    // Analyser setup
    this.analyser.fftSize = this.fftSize;
    this.analyser.smoothingTimeConstant = this.smoothing;

    // Buffers
    this.freqBins = new Float32Array(this.analyser.frequencyBinCount);
    this.timeBins = new Float32Array(this.analyser.fftSize);

    // State
    this.lastPhoneme = "MBP";
    this.lastEmitAt = 0;
    this._gateOpen = false;
    this._lastVoiceMs = 0;
    this.running = false;
    this._tick = this._tick.bind(this);

    if (this.debug) {
      console.log("[LipTap] init v2.1", {
        sampleRate: this.audioCtx.sampleRate,
        fftSize: this.fftSize,
        useHnr: this.useHnr,
        hnrEveryNFrames: this.hnrEveryNFrames,
        hnrBufferSamples: this.hnrBufferSamples,
        useVarianceGate: this.useVarianceGate,
        burstLoFactor: this.burstLoFactor,
        wwwMaxFrames: this.wwwMaxFrames,
        wwwCooldownMs: this.wwwCooldownMs,
      });
    }
  }

  // ─────────────────────────────── Public API ────────────────────────────────

  async start() {
    if (this.running) return;
    this.running = true;
    this._lastVoiceMs = performance.now();
    if (this.debug) console.log("[LipTap] start()");
    if (this.autoCalibrate && !this._calibrated) {
      this.calibrateNoise(this.calibrateMs).catch(function () {});
    }
    requestAnimationFrame(this._tick);
  }

  stop() {
    try {
      this.lips.zeroAllPhonemes({ keepMouth: false });
    } catch (e) {}
    if (this.debug) console.log("[LipTap] stop()");
    this.running = false;
  }

  async calibrateNoise(ms) {
    ms = ms === undefined ? 400 : ms;
    const samples = [];
    const t0 = performance.now();

    while (performance.now() - t0 < ms) {
      this.analyser.getFloatTimeDomainData(this.timeBins);
      const r = this._rms(this.timeBins);
      if (r < 0.02) samples.push(r);
      await new Promise(function (rf) {
        requestAnimationFrame(rf);
      });
    }

    if (samples.length === 0) {
      if (this.debug) console.log("[LipTap] calibration skipped (no silence)");
      this._calibrated = true;
      return;
    }

    samples.sort(function (a, b) {
      return a - b;
    });
    const pick = samples[Math.max(0, Math.floor(samples.length * 0.2) - 1)];
    const base = Math.max(0.0035, pick);

    const newMin = base * 1.2;
    const newOpen = newMin * 1.5;
    const newClose = newMin * 1.08;

    const start = performance.now(),
      dur = 250;
    const sMin = this.minRms,
      sOpen = this.gateOpenRms,
      sClose = this.gateCloseRms;
    const lerp = function (a, b, t) {
      return a + (b - a) * t;
    };

    const step = () => {
      const t = Math.min(1, (performance.now() - start) / dur);
      this.minRms = lerp(sMin, newMin, t);
      this.gateOpenRms = lerp(sOpen, newOpen, t);
      this.gateCloseRms = lerp(sClose, newClose, t);
      if (t < 1) requestAnimationFrame(step);
    };
    requestAnimationFrame(step);

    this._calibrated = true;
    if (this.debug)
      console.log("[LipTap] calibrated", {
        samples: samples.length,
        base: this._f(base),
        minRms: this._f(newMin),
        open: this._f(newOpen),
        close: this._f(newClose),
      });
  }

  // ─────────────────────────────── Core tick ─────────────────────────────────

  _tick() {
    if (!this.running) return;

    this.analyser.getFloatTimeDomainData(this.timeBins);
    this.analyser.getFloatFrequencyData(this.freqBins);

    const now = performance.now();
    const rms = this._rms(this.timeBins);
    const rmsDelta = rms - this._lastRms;
    this._lastRms = rms;

    // Rolling RMS history for variance gate
    this._rmsHistory.push(rms);
    if (this._rmsHistory.length > this.varianceWindowSize)
      this._rmsHistory.shift();

    // Hysteresis gate
    if (!this._gateOpen) {
      if (rms >= this.gateOpenRms) {
        this._gateOpen = true;
        this._lastVoiceMs = now;
      }
    } else {
      if (rms >= this.gateCloseRms) this._lastVoiceMs = now;
      else if (now - this._lastVoiceMs > this.silenceHoldMs)
        this._gateOpen = false;
    }

    // Idle auto-stop
    if (
      !this._gateOpen &&
      this.idleStopMs !== null &&
      now - this._lastVoiceMs > this.idleStopMs
    ) {
      if (this.debug) console.log("[LipTap] idle stop");
      this.stop();
      return;
    }

    // Mouth drive
    if (this.enableMouthDrive && this.lips && this.lips.applyPhoneme) {
      const mTarget = this._intensityFromRms(rms) * this.mouthDriveGain;
      const kA = 1 - Math.exp(-this.frameMs / this.mouthAttackMs);
      const kR = 1 - Math.exp(-this.frameMs / this.mouthReleaseMs);
      const k = mTarget > this._mouthEnv ? kA : kR;
      this._mouthEnv += (mTarget - this._mouthEnv) * k;
      try {
        this.lips.applyPhoneme("MOUTH", Math.min(1, this._mouthEnv));
      } catch (e) {}
    }

    // Silence path
    if (!this._gateOpen) {
      try {
        if (this.lips && typeof this.lips.zeroAllPhonemes === "function") {
          this.lips.zeroAllPhonemes({ keepMouth: this.enableMouthDrive });
        }
      } catch (e) {}
      requestAnimationFrame(this._tick);
      return;
    }

    // ── Spectral features ──────────────────────────────────────────────────

    const sr = this.audioCtx.sampleRate;
    const spec = this._melishBands(this.freqBins, sr);
    const hiOverMid = (spec.hi + 1e-6) / (spec.mid + 1e-6);
    const loOverMid = (spec.lo + 1e-6) / (spec.mid + 1e-6);
    const centroid = this._centroid(this.freqBins, sr) + this.centroidShift;
    const intensity = this._intensityFromRms(rms);

    // Adaptive low-band baseline (v2.1)
    this._loBase =
      this._loBase * this.loBaseSmooth + spec.lo * (1 - this.loBaseSmooth);

    // ZCR voicing
    const zcr = this.useZcr ? this._zcr(this.timeBins) : 0;

    // HNR voicing (v2.1 throttled + smoothed)
    let hnr = this._hnrSmoothed;
    if (this.useHnr) {
      this._hnrFrame++;
      if (this._hnrFrame % this.hnrEveryNFrames === 0) {
        const short =
          this.hnrBufferSamples && this.hnrBufferSamples < this.timeBins.length
            ? this.timeBins.subarray(
                this.timeBins.length - this.hnrBufferSamples,
              )
            : this.timeBins;

        const raw = this._hnrProxy(short);
        this._hnrSmoothed =
          this._hnrSmoothed * this.hnrSmooth + raw * (1 - this.hnrSmooth);
      }
      hnr = this._hnrSmoothed;
    } else {
      hnr = 0.5;
    }

    const isVoiced = hnr > this.hnrVoicedThreshold;
    const voiced =
      isVoiced || (this.useZcr ? zcr < this.zcrThreshold * 1.5 : true);

    // Variance gate
    const rmsVariance = this.useVarianceGate
      ? this._variance(this._rmsHistory)
      : 1;
    const varGatePass = rmsVariance > this.varianceMinThresh;

    // Formants (v2.1: gated + smoothed in freq + smoothed over time)
    let formants = { f1: 0, f2: 0, f3: 0 };

    if (voiced && intensity >= this.formantMinIntensity && varGatePass) {
      const rawF = this._estimateFormants(this.freqBins, sr);

      // temporal smoothing
      const a = this.formantTemporalSmooth; // keep old weight
      formants = {
        f1: this._lastFormants.f1 * a + rawF.f1 * (1 - a),
        f2: this._lastFormants.f2 * a + rawF.f2 * (1 - a),
        f3: this._lastFormants.f3 * a + rawF.f3 * (1 - a),
      };
      this._lastFormants = formants;
    } else {
      // keep last known formants a bit, but decay toward 0
      const a = 0.85;
      this._lastFormants = {
        f1: this._lastFormants.f1 * a,
        f2: this._lastFormants.f2 * a,
        f3: this._lastFormants.f3 * a,
      };
      formants = this._lastFormants;
    }

    if (this.debug && Math.random() < 0.04) {
      console.log("[LipTap] probe", {
        rms: this._f(rms),
        zcr: this._f(zcr),
        hnr: this._f(hnr),
        centroid: Math.round(centroid),
        f1: Math.round(formants.f1),
        f2: Math.round(formants.f2),
        f3: Math.round(formants.f3),
        lo: this._f(spec.lo),
        mid: this._f(spec.mid),
        hi: this._f(spec.hi),
        loOverMid: this._f(loOverMid),
        hiOverMid: this._f(hiOverMid),
        voiced,
        varGatePass,
        loBase: this._f(this._loBase),
      });
    }

    // ── Onset MBP pop ──────────────────────────────────────────────────────

    if (this.enableOnsetMbp) {
      const cooled = now - this._lastOnsetAt > this.onsetCooldownMs;
      const strongAttack = rmsDelta > this.onsetRmsDelta && intensity > 0.55;
      const notHissy = hiOverMid < this.hissRatio * 0.95;

      // v2.1: adaptive lo-burst (baseline * factor). Also keep your absolute fallback.
      const loBurstAdaptive =
        this._loBase > 0 ? spec.lo > this._loBase * this.burstLoFactor : false;
      const loBurst = loBurstAdaptive || spec.lo > this.burstLoThreshold;

      if (cooled && strongAttack && notHissy && loBurst) {
        this._lastOnsetAt = now;
        this._emit("MBP", Math.min(1, 0.75 + intensity * 0.25), now, {
          gate: "onset-mbp",
          rmsDelta: this._f(rmsDelta),
          lo: this._f(spec.lo),
          loBurstAdaptive,
        });
        requestAnimationFrame(this._tick);
        return;
      }
    }

    // ── Fricative detection ────────────────────────────────────────────────

    if (
      hiOverMid > this.hissRatio &&
      (!this.useZcr || zcr > this.zcrThreshold)
    ) {
      let fricPhoneme;
      if (spec.hi2 > spec.hi1 * 1.2) {
        fricPhoneme = "SSS";
      } else if (centroid < 3200 && spec.hi1 > spec.hi2 * 1.1) {
        fricPhoneme = "FFF";
      } else {
        fricPhoneme = "TTH";
      }

      // WWW streak reset on fricatives
      this._wwwFrames = 0;

      this._emit(fricPhoneme, intensity, now, {
        gate: "fricative",
        hiOverMid: this._f(hiOverMid),
        zcr: this._f(zcr),
        fricPhoneme,
      });
      requestAnimationFrame(this._tick);
      return;
    }

    // ── TLDN detection ─────────────────────────────────────────────────────

    const midPeak = this._bandPeak(this.freqBins, sr, 1000, 2800);
    const isMidDominant =
      spec.mid > spec.lo * 1.18 && centroid > 1200 && centroid < 2600;

    const tldnConfidence =
      isMidDominant &&
      formants.f2 > 1400 &&
      formants.f2 < 3000 &&
      midPeak.sharpness > 0.3 &&
      voiced;

    // ── Vowel classification via formants ─────────────────────────────────

    let vowelByFormant = "AAA";
    if (varGatePass && voiced && intensity >= this.formantMinIntensity) {
      const f1 = formants.f1;
      const f2 = formants.f2;

      if (f1 > this.f1Mid) {
        vowelByFormant = f2 > this.f2Mid ? "AAA" : "OHH";
      } else if (f1 > this.f1Low) {
        vowelByFormant =
          f2 > this.f2Mid ? "EH" : f2 > this.f2Low ? "AAA" : "OHH";
      } else {
        vowelByFormant =
          f2 > this.f2High ? "IEE" : f2 > this.f2Mid ? "EH" : "UUU";
      }
    } else {
      if (centroid < this.vowelC1) vowelByFormant = "OHH";
      else if (centroid < this.vowelC2) vowelByFormant = "UUU";
      else if (centroid < this.vowelC3) vowelByFormant = "EH";
      else vowelByFormant = "IEE";
    }

    // Rounded/back vowel check
    const roundedVowel =
      loOverMid > this.roundLoOverMid &&
      centroid < this.vowelC2 + this.roundCentroidPad &&
      hiOverMid < this.hissRatio * 0.95;

    // ── WWW (keep WWW, but brief-limited) ──────────────────────────────────
    // /w/ is usually voiced, so do NOT require !voiced.
    // Instead: require strong low dominance + low centroid + not hissy + not TLDN,
    // and enforce "brief" by max frames + cooldown.
    const rawShouldWWW =
      loOverMid > this.wwwLoOverMid &&
      centroid < this.wwwCentroidMax &&
      !tldnConfidence &&
      hiOverMid < this.hissRatio * 0.9;

    const wwwCooled = now - this._lastWwwAt > this.wwwCooldownMs;
    const shouldWWW =
      rawShouldWWW && wwwCooled && this._wwwFrames < this.wwwMaxFrames;

    // ── RRR ──────────────────────────────────────────────────────────────

    const shouldRRR =
      spec.midOverHi > 1.25 &&
      centroid > 1400 &&
      centroid < 3000 &&
      formants.f3 > 1800 &&
      formants.f3 < 2500 &&
      !roundedVowel &&
      !tldnConfidence &&
      voiced;

    // ── Pick phoneme ──────────────────────────────────────────────────────

    let phoneme;

    if (tldnConfidence) {
      phoneme = "TLDN";
    } else if (shouldRRR) {
      phoneme = "RRR";
    } else if (shouldWWW) {
      phoneme = "WWW";
    } else if (roundedVowel) {
      phoneme = centroid < this.vowelC1 ? "OHH" : "UUU";
    } else {
      phoneme = vowelByFormant;
    }

    // Track WWW brief streak
    if (phoneme === "WWW") {
      this._wwwFrames++;
      this._lastWwwAt = now;
    } else {
      this._wwwFrames = 0;
    }

    // ── Dual-pool score smoothing ─────────────────────────────────────────

    if (this.smoothScores) {
      const isVowelPhoneme = phoneme in this._vowelScores;
      const decay = Math.min(0.92, Math.max(0.65, this.scoreDecay));

      const vKeys = Object.keys(this._vowelScores);
      for (let i = 0; i < vKeys.length; i++)
        this._vowelScores[vKeys[i]] *= decay;

      const cKeys = Object.keys(this._consScores);
      for (let i = 0; i < cKeys.length; i++)
        this._consScores[cKeys[i]] *= decay;

      if (isVowelPhoneme) {
        this._vowelScores[phoneme] +=
          1.0 * this.scoreBoost * (this.phonemeBias[phoneme] || 1);

        if (vowelByFormant !== phoneme && vowelByFormant in this._vowelScores) {
          this._vowelScores[vowelByFormant] += 0.2 * this.scoreBoost;
        }

        if (phoneme === this.lastPhoneme) this._vowelScores[phoneme] *= 0.9;
      } else {
        this._consScores[phoneme] +=
          1.0 * this.scoreBoost * (this.phonemeBias[phoneme] || 1);

        if (phoneme === this.lastPhoneme) this._consScores[phoneme] *= 0.9;
      }

      let bestVowel = "AAA",
        bestVowelV = -1;
      for (let i = 0; i < vKeys.length; i++) {
        const v =
          this._vowelScores[vKeys[i]] * (this.phonemeBias[vKeys[i]] || 1);
        if (v > bestVowelV) {
          bestVowelV = v;
          bestVowel = vKeys[i];
        }
      }

      let bestCons = "MBP",
        bestConsV = -1;
      for (let i = 0; i < cKeys.length; i++) {
        const v =
          this._consScores[cKeys[i]] * (this.phonemeBias[cKeys[i]] || 1);
        if (v > bestConsV) {
          bestConsV = v;
          bestCons = cKeys[i];
        }
      }

      const consWinMargin = 0.25;
      if (
        !(phoneme in this._vowelScores) &&
        bestConsV > bestVowelV * (1 + consWinMargin)
      ) {
        phoneme = bestCons;
      } else {
        phoneme = bestVowel;
      }

      this._consecutiveWins[phoneme] =
        (this._consecutiveWins[phoneme] || 0) + 1;
      if (this._consecutiveWins[phoneme] > this._maxConsecutive) {
        if (phoneme in this._vowelScores) this._vowelScores[phoneme] *= 0.6;
        else this._consScores[phoneme] *= 0.6;
        this._consecutiveWins[phoneme] = 0;
      }

      const allPhonemes = [...vKeys, ...cKeys];
      for (let i = 0; i < allPhonemes.length; i++) {
        if (allPhonemes[i] !== phoneme)
          this._consecutiveWins[allPhonemes[i]] = 0;
      }
    }

    this._emit(phoneme, intensity, now, {
      centroid: Math.round(centroid),
      f1: Math.round(formants.f1),
      f2: Math.round(formants.f2),
      f3: Math.round(formants.f3),
      hnr: this._f(hnr),
      voiced,
      varGatePass,
      spec: {
        lo: this._f(spec.lo),
        mid: this._f(spec.mid),
        hi: this._f(spec.hi),
        midOverHi: this._f(spec.midOverHi),
        loOverMid: this._f(loOverMid),
        hiOverMid: this._f(hiOverMid),
      },
    });

    requestAnimationFrame(this._tick);
  }

  // ─────────────────────────────── Emit ─────────────────────────────────────

  _emit(phoneme, intensity, now, extras) {
    extras = extras || {};
    const isMouth = phoneme === "MOUTH";
    const minGap = Math.max(this.debounceMs, this.minChangeMs);

    if (!isMouth) {
      if (phoneme !== this.lastPhoneme && now - this.lastEmitAt < minGap)
        return;
      this.lastPhoneme = phoneme;
      this.lastEmitAt = now;
    }

    if (this.debug) {
      console.log(
        "[LipTap] emit",
        Object.assign({ phoneme, intensity: this._f(intensity) }, extras),
      );
    }

    try {
      this.lips.applyPhoneme(phoneme, intensity);
    } catch (e) {
      console.warn("[LipTap] applyPhoneme error:", e);
    }
  }

  // ─────────────────────────────── DSP Helpers ──────────────────────────────

  _intensityFromRms(rms) {
    const x = Math.max(
      0,
      Math.min(1, (rms - this.minRms) / (0.25 - this.minRms)),
    );
    const shaped = Math.pow(x, 0.75);
    return Math.min(1, 0.45 + shaped * this.intensityGain * 0.55);
  }

  _rms(buf) {
    let s = 0;
    for (let i = 0; i < buf.length; i++) s += buf[i] * buf[i];
    return Math.sqrt(s / buf.length);
  }

  _zcr(buf) {
    let z = 0;
    for (let i = 1; i < buf.length; i++) {
      if (buf[i - 1] >= 0 !== buf[i] >= 0) z++;
    }
    return z / buf.length;
  }

  _mag(db) {
    const lin = Math.pow(10, db / 20);
    return isFinite(lin) ? Math.max(0, lin) : 0;
  }

  _f(x) {
    return Number(x).toFixed(3);
  }

  _variance(arr) {
    if (arr.length < 2) return 0;
    let sum = 0;
    for (let i = 0; i < arr.length; i++) sum += arr[i];
    const mean = sum / arr.length;
    let vr = 0;
    for (let i = 0; i < arr.length; i++)
      vr += (arr[i] - mean) * (arr[i] - mean);
    return vr / arr.length;
  }

  _centroid(fd, sr) {
    const hz = sr / (2 * fd.length);
    let n = 0,
      d = 0;
    for (let i = 0; i < fd.length; i++) {
      const m = this._mag(fd[i]);
      n += i * hz * m;
      d += m;
    }
    return d > 0 ? n / d : 0;
  }

  _melishBands(fd, sr) {
    const hz = sr / (2 * fd.length);
    const rng = function (lo, hi) {
      const li = Math.max(0, Math.floor(lo / hz));
      const hiI = Math.min(fd.length - 1, Math.ceil(hi / hz));
      let sum = 0,
        c = 0;
      for (let i = li; i <= hiI; i++) {
        sum += Math.max(0, Math.pow(10, fd[i] / 20));
        c++;
      }
      return sum / Math.max(1, c);
    };

    const lo = rng(80, 500);
    const mid = rng(500, 2500);
    const hi1 = rng(2500, 4500);
    const hi2 = rng(4500, 8000);
    const hi = hi1 + hi2;

    return { lo, mid, hi1, hi2, hi, midOverHi: mid / (hi + 1e-6) };
  }

  /**
   * Estimate F1, F2, F3 from frequency domain using peak picking.
   * v2.1: includes tiny spectrum smoothing to reduce harmonic locking.
   */
  _estimateFormants(fd, sr) {
    const hz = sr / (2 * fd.length);
    const magBuf = new Float32Array(fd.length);
    for (let i = 0; i < fd.length; i++) magBuf[i] = this._mag(fd[i]);

    // v2.1: tiny 3-bin smoothing (repeatable if you want stronger)
    const passes = Math.max(0, this.formantSmoothBins | 0);
    for (let p = 0; p < passes; p++) {
      for (let i = 1; i < magBuf.length - 1; i++) {
        magBuf[i] = (magBuf[i - 1] + magBuf[i] + magBuf[i + 1]) / 3;
      }
    }

    const peakInRange = (loHz, hiHz) => {
      const li = Math.max(1, Math.floor(loHz / hz));
      const hiI = Math.min(fd.length - 2, Math.ceil(hiHz / hz));
      let bestV = -1,
        bestI = li;

      for (let i = li; i <= hiI; i++) {
        if (magBuf[i] > bestV) {
          bestV = magBuf[i];
          bestI = i;
        }
      }

      const y0 = magBuf[bestI - 1] || 0;
      const y1 = magBuf[bestI];
      const y2 = magBuf[bestI + 1] || 0;
      const denom = y0 - 2 * y1 + y2;
      const offset = denom !== 0 ? (0.5 * (y0 - y2)) / denom : 0;
      return (bestI + offset) * hz;
    };

    return {
      f1: peakInRange(200, 900),
      f2: peakInRange(700, 2800),
      f3: peakInRange(2000, 3500),
    };
  }

  /**
   * HNR proxy via normalized autocorrelation.
   */
  _hnrProxy(buf) {
    const sr = this.audioCtx.sampleRate;
    const minLag = Math.floor(sr / 400);
    const maxLag = Math.ceil(sr / 80);
    const len = buf.length;

    let r0 = 0;
    for (let i = 0; i < len; i++) r0 += buf[i] * buf[i];
    if (r0 < 1e-10) return 0;

    let maxR = 0;
    for (let lag = minLag; lag <= Math.min(maxLag, len - 1); lag++) {
      let r = 0;
      const limit = len - lag;
      for (let i = 0; i < limit; i++) r += buf[i] * buf[i + lag];
      const rNorm = r / r0;
      if (rNorm > maxR) maxR = rNorm;
    }

    return Math.max(0, Math.min(1, maxR));
  }

  /**
   * Spectral peak sharpness in a band (used for TLDN detection).
   */
  _bandPeak(fd, sr, loHz, hiHz) {
    const hz = sr / (2 * fd.length);
    const li = Math.max(0, Math.floor(loHz / hz));
    const hiI = Math.min(fd.length - 1, Math.ceil(hiHz / hz));

    let sum = 0,
      peakV = -Infinity,
      peakI = li;
    for (let i = li; i <= hiI; i++) {
      const m = this._mag(fd[i]);
      sum += m;
      if (m > peakV) {
        peakV = m;
        peakI = i;
      }
    }

    const count = hiI - li + 1;
    const avg = sum / Math.max(1, count);
    const sharpness = avg > 0 ? Math.min(1, (peakV - avg) / avg) : 0;

    return { peak: peakI * hz, sharpness };
  }
}
